import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, Phone, Calendar, MessageSquare } from 'lucide-react';
import serviceFeeFaqImg from '../../assets/serviceFeeFaqImg.jpg';
import { useNavigate } from 'react-router';

const faqData = [
  {
    question: "Do you accept insurance?",
    answer: "We are a fee-for-service provider, which means we are out-of-network with insurance. However, we're happy to help file claims for reimbursement."
  },
  {
    question: "Why don't you list specific fees?",
    answer: "Each treatment is tailored to your needs. We provide a detailed, personalized estimate after your consultation."
  },
  {
    question: "How do I know what my treatment will cost?",
    answer: "We'll provide a clear breakdown of your recommended treatment and cost before any procedure begins."
  }
];

export function ServiceFeeFAQ() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const sectionRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          // Animate image first
          const image = document.querySelector('.faq-image');
          if (image) {
            image.classList.add('animate-in');
          }
          
          // Animate heading
          setTimeout(() => {
            const heading = document.querySelector('.faq-heading');
            if (heading) {
              heading.classList.add('animate-in');
            }
          }, 200);
          
          // Animate FAQ items with staggered delay
          const faqItems = document.querySelectorAll('.faq-item');
          faqItems.forEach((item, index) => {
            setTimeout(() => {
              item.classList.add('animate-in');
            }, 400 + (index * 150));
          });
          
          // Animate CTA
          setTimeout(() => {
            const cta = document.querySelector('.faq-cta');
            if (cta) {
              cta.classList.add('animate-in');
            }
          }, 1000);
        }
      },
      { threshold: 0.1 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-6 relative overflow-hidden bg-gradient-to-b from-teal-50 to-white">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-teal-100 to-transparent opacity-30"></div>
      
      <div className="max-w-6xl mx-auto">
        <h2 className="faq-heading text-3xl font-bold text-center bg-gradient-to-r from-teal-600 to-green-600 bg-clip-text text-transparent mb-16 opacity-0 transform -translate-y-4 transition-all duration-700">
          Frequently Asked Questions
        </h2>
        
        <div className="flex flex-col md:flex-row gap-10 items-center md:items-start">
          {/* Image side */}
          <div className="faq-image w-full md:w-2/5 opacity-0 transition-all duration-700">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-green-200 rounded-full opacity-30 z-0"></div>
              <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-teal-200 rounded-full opacity-30 z-0"></div>
              
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-xl border-4 border-white">
                <img 
                  src={serviceFeeFaqImg} 
                  alt="Dental care professional with patient" 
                  className="w-full object-cover h-full"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 to-transparent"></div>
                <div className="absolute bottom-0 left-0 w-full p-6 text-white">
                  <h3 className="text-2xl font-bold mb-2">Personalized Care</h3>
                  <p className="text-white/90">We're committed to transparent pricing and excellent service</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* FAQ Accordion side */}
          <div className="w-full md:w-3/5">
            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <div 
                  key={index} 
                  className={`faq-item border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 opacity-0 transform translate-y-8`}
                  style={{ transitionProperty: 'opacity, transform', transitionDuration: '0.5s' }}
                >
                  <button
                    onClick={() => setOpen(open === index ? null : index)}
                    className={`w-full px-6 py-4 flex justify-between items-center transition ${open === index ? 'bg-gradient-to-r from-teal-50 to-green-50 text-teal-800' : 'bg-white hover:bg-gray-50'}`}
                  >
                    <span className="text-left font-medium text-gray-800">{faq.question}</span>
                    <div className={`flex-shrink-0 ml-4 p-1 rounded-full ${open === index ? 'bg-teal-100 text-teal-600' : 'text-gray-400'}`}>
                      {open === index ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                  </button>
                  <div 
                    className={`overflow-hidden transition-all duration-300 ${open === index ? 'max-h-40' : 'max-h-0'}`}
                  >
                    <div className="px-6 py-4 text-gray-600 bg-white border-t border-gray-100">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Call to Action */}
            <div className="faq-cta mt-12 p-6 bg-gradient-to-r from-teal-500 to-green-500 rounded-xl shadow-lg text-white opacity-0 transform translate-y-8 transition-all duration-700">
              <h3 className="text-xl font-semibold mb-3">Ready to experience transparent dental care?</h3>
              <p className="mb-6">Schedule your consultation today and get a personalized treatment plan with clear pricing.</p>
              
              <div className="flex flex-wrap gap-4">
                <button className="flex items-center px-4 py-2 bg-white text-teal-600 rounded-lg font-medium hover:bg-teal-50 transition-colors duration-300">
                  <Phone size={18} className="mr-2" />
                  Call Now
                </button>
                <button onClick={() => navigate('/schedule-appointment')} className="cursor-pointer flex items-center px-4 py-2 bg-white text-teal-600 rounded-lg font-medium hover:bg-teal-50 transition-colors duration-300">
                  <Calendar size={18} className="mr-2" />
                  Book Online
                </button>
                <button className="flex items-center px-4 py-2 bg-white text-teal-600 rounded-lg font-medium hover:bg-teal-50 transition-colors duration-300">
                  <MessageSquare size={18} className="mr-2" />
                  Ask a Question
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Add this CSS to your global styles or component styles
const styles = `
  .faq-heading.animate-in,
  .faq-image.animate-in,
  .faq-item.animate-in,
  .faq-cta.animate-in {
    opacity: 1;
    transform: translateY(0) translateX(0);
  }
  
  @keyframes pulse-subtle {
    0% { box-shadow: 0 0 0 0 rgba(45, 212, 191, 0.2); }
    70% { box-shadow: 0 0 0 10px rgba(45, 212, 191, 0); }
    100% { box-shadow: 0 0 0 0 rgba(45, 212, 191, 0); }
  }
`;

export default function StyledServiceFeeFAQ() {
  return (
    <>
      <style>{styles}</style>
      <ServiceFeeFAQ />
    </>
  );
}